/**
 * REACTIVATE ORGANIZATION DIALOG
 *
 * Confirmation dialog shown before reactivating an archived organization.
 * Allows the ops user to select which locations to reactivate.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Info, Loader2, XCircle, MapPin } from 'lucide-react';
import { reactivateOrganization } from '@/lib/onboarding-status';
import { useAuth } from '@/hooks/use-auth';
import type { ArchivedLocation } from '@/hooks/use-main-app-archived-orgs';

interface ReactivateOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  organizationName: string;
  // archivedLocationCount: number;
  archivedLocations: ArchivedLocation[];
  /** 'archive_pending' = subscription still alive (will be resumed); 'archived' = subscription gone (new one created) */
  orgStatus: 'archive_pending' | 'archived';
  onSuccess: () => void;
}

export function ReactivateOrgDialog({
  open,
  onOpenChange,
  organizationId,
  organizationName,
  // archivedLocationCount,
  archivedLocations,
  orgStatus: _orgStatus,
  onSuccess,
}: ReactivateOrgDialogProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(archivedLocations.map((l) => l.id)));
      setError(null);
    }
  }, [open, archivedLocations]);

  const allSelected = selectedIds.size === archivedLocations.length;
  const noneSelected = selectedIds.size === 0;

  const toggleLocation = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(archivedLocations.map((l) => l.id)));
    }
  };

  const handleReactivate = async () => {
    if (!user?.id) {
      setError('Unable to determine current user');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Send selected location IDs (omit if all selected for backward compatibility)
      const locationIds = allSelected ? undefined : Array.from(selectedIds);
      const result = await reactivateOrganization(organizationId, user.id, locationIds);

      if (!result.success) {
        throw new Error(result.error ?? 'Reactivation failed');
      }

      // Show warning if some locations couldn't be restored
      if (result.failed_locations && result.failed_locations.length > 0) {
        const names = result.failed_locations.map((f) => f.location_name).join(', ');
        setError(
          `Organization reactivated, but ${result.failed_locations.length} location(s) could not be restored (${names}). ` +
          `Reason: ${result.failed_locations[0].reason}. ` +
          `These locations remain archived — fix the payment method and retry from the pipeline table.`
        );
        onSuccess(); // still refresh data
        // Don't close dialog so user can read the warning
        return;
      }

      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to reactivate organization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-green-700 dark:text-green-400'>
            <RefreshCw className='h-5 w-5' />
            Reactivate Organization
          </DialogTitle>
          <DialogDescription className='text-base font-medium text-gray-900 dark:text-gray-100 mt-1'>
            {organizationName}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          <p className='text-sm text-gray-700 dark:text-gray-300'>
            This will restore <strong>{organizationName}</strong> and{' '}
            <strong>
              {selectedIds.size} of {archivedLocations.length} location
              {archivedLocations.length !== 1 ? 's' : ''}
            </strong>{' '}
            immediately.
          </p>

          {/* Location selection with checkboxes — same UI for both archive_pending and archived */}
          {archivedLocations.length > 0 && (
            <div className='rounded-md border border-gray-200 dark:border-gray-700'>
              <div className='flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50'>
                <span className='text-xs font-medium text-gray-600 dark:text-gray-400'>
                  {selectedIds.size} of {archivedLocations.length} selected
                </span>
                <button
                  type='button'
                  onClick={toggleAll}
                  className='text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <ScrollArea className='max-h-[200px]'>
                <div className='divide-y divide-gray-100 dark:divide-gray-800'>
                  {archivedLocations.map((loc) => (
                    <label
                      key={loc.id}
                      className='flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/30'
                    >
                      <Checkbox
                        checked={selectedIds.has(loc.id)}
                        onCheckedChange={() => toggleLocation(loc.id)}
                      />
                      {loc.logo_url ? (
                        <div className='w-6 h-6 rounded overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800'>
                          <img
                            src={loc.logo_url}
                            alt={loc.name}
                            className='w-full h-full object-cover'
                          />
                        </div>
                      ) : (
                        <MapPin className='h-4 w-4 text-gray-400 shrink-0' />
                      )}
                      <span className='text-sm text-gray-800 dark:text-gray-200 truncate flex-1'>
                        {loc.name}
                      </span>
                      {loc.archived_at ? (
                        <Badge className='bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 text-[10px] px-1.5 py-0 shrink-0'>
                          ARCHIVED
                        </Badge>
                      ) : (
                        <Badge className='bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-[10px] px-1.5 py-0 shrink-0'>
                          PENDING
                        </Badge>
                      )}
                    </label>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          <div className='flex gap-3 rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950/30'>
            <Info className='h-4 w-4 shrink-0 mt-0.5 text-blue-600 dark:text-blue-400' />
            <p className='text-sm text-blue-800 dark:text-blue-300'>
              {(() => {
                const selectedLocs = archivedLocations.filter((l) => selectedIds.has(l.id));
                const pendingCount = selectedLocs.filter((l) => !l.archived_at).length;
                const archivedCount = selectedLocs.filter((l) => l.archived_at).length;

                if (pendingCount > 0 && archivedCount > 0) {
                  return (
                    <>
                      {pendingCount} location{pendingCount !== 1 ? 's' : ''} will have{' '}
                      {pendingCount !== 1 ? 'their' : 'its'} pending cancellation reversed
                      (billing continues). {archivedCount} location{archivedCount !== 1 ? 's' : ''}{' '}
                      will get new subscription{archivedCount !== 1 ? 's' : ''} created using the
                      payment method on file.
                    </>
                  );
                }
                if (archivedCount > 0) {
                  return (
                    <>
                      New Stripe subscriptions will be created automatically using the payment
                      method on file. Users will have immediate access once reactivation
                      completes.
                    </>
                  );
                }
                return (
                  <>
                    The pending cancellation will be reversed and the existing subscription
                    {pendingCount !== 1 ? 's' : ''} resumed. No new charges will be
                    created — billing continues from where it left off.
                  </>
                );
              })()}
            </p>
          </div>

          {error && (
            <div className='flex gap-3 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30'>
              <XCircle className='h-4 w-4 shrink-0 mt-0.5 text-red-600 dark:text-red-400' />
              <p className='text-sm text-red-800 dark:text-red-300'>{error}</p>
            </div>
          )}
        </div>

        <DialogFooter className='gap-2'>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant='default'
            className='bg-green-600 hover:bg-green-700 text-white'
            onClick={handleReactivate}
            disabled={loading || noneSelected}
          >
            {loading ? (
              <>
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                Reactivating…
              </>
            ) : (
              <>
                <RefreshCw className='h-4 w-4 mr-2' />
                Reactivate{selectedIds.size < archivedLocations.length ? ` (${selectedIds.size})` : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
